import React from "react";
import { Link } from "react-router-dom";
import ADNavbar from "../components/ADNavbar";
import SiteFooter from "../components/SiteFooter";
import { Home, TrendingUp, Lightbulb } from "lucide-react";

const IMG = {
  hero: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80",
  g1: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&q=80",
  g2: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80",
  g3: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=600&q=80",
  g4: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=600&q=80",
};

const RealtyPage = () => (
  <>
    <ADNavbar />

    {/* Hero */}
    <section className="pt-20 relative overflow-hidden" style={{ backgroundColor: "#0B1628" }}>
      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center">
        <div className="lg:w-1/2 px-6 lg:px-12 py-20 lg:py-28">
          <p className="text-[11px] tracking-[0.3em] uppercase mb-6" style={{ color: "#999" }}>Real Estate</p>
          <h1
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-[1.1]"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Adris Davis Realty
          </h1>
          <div className="w-10 h-px mb-6" style={{ backgroundColor: "rgba(255,255,255,0.15)" }} />
          <p className="text-sm text-white/50 leading-[1.8] mb-4">
            Strategic real estate guidance built on experience, negotiation, and results.
          </p>
          <p className="text-xs text-white/30 leading-[1.8]">
            Helping clients navigate real estate decisions with clarity, confidence, and execution.
          </p>
        </div>
        <div className="lg:w-1/2 h-64 lg:h-auto">
          <img src={IMG.hero} alt="Real Estate" className="w-full h-full object-cover lg:min-h-[450px]" />
        </div>
      </div>
    </section>

    {/* Overview */}
    <section className="py-20 px-6">
      <div className="max-w-3xl mx-auto">
        <h2
          className="text-2xl md:text-3xl font-bold text-gray-900 mb-8"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Real Estate with a Strategic Approach
        </h2>
        <div className="text-gray-500 text-sm leading-[1.9] space-y-5">
          <p>
            Adris Davis Realty is built on the same principles that drive success in business—strategy, negotiation, and execution. With a background in high-level sales and deal structuring, Adris brings a results-driven approach to real estate, helping clients make confident and informed decisions.
          </p>
          <p>
            Whether buying, selling, or evaluating opportunities, the focus is always on positioning, timing, and maximizing outcomes.
          </p>
        </div>
      </div>
    </section>

    {/* Services */}
    <section className="py-20 px-6" style={{ backgroundColor: "#FAFAFA" }}>
      <div className="max-w-5xl mx-auto">
        <h2
          className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-14"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          What We Do
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Home, title: "Buying", text: "Guidance through the home buying process with a focus on value, negotiation, and long-term positioning." },
            { icon: TrendingUp, title: "Selling", text: "Strategic listing and positioning designed to attract serious buyers and maximize return." },
            { icon: Lightbulb, title: "Consulting", text: "Real estate insight for investors and individuals looking to make smarter property decisions." },
          ].map((card, i) => (
            <div
              key={i}
              className="bg-white border border-gray-100 p-8 text-center transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: "#f5f5f5" }}>
                <card.icon size={22} className="text-gray-700" />
              </div>
              <h3
                className="text-base font-bold text-gray-900 mb-3"
                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              >
                {card.title}
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed">{card.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* The Difference */}
    <section className="py-20 px-6">
      <div className="max-w-3xl mx-auto">
        <h2
          className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          The Difference
        </h2>
        <p className="text-sm text-gray-400 italic mb-10">
          Real estate is a sales game—and positioning wins.
        </p>
        <div className="space-y-4">
          {[
            "Negotiation-driven mindset",
            "Strong understanding of market positioning",
            "Sales expertise applied to real estate transactions",
            "Focus on results, not just transactions",
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-4 py-3 border-b border-gray-100 last:border-0">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
              <p className="text-sm text-gray-600">{item}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Gallery */}
    <section className="py-0">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
        {[IMG.g1, IMG.g2, IMG.g3, IMG.g4].map((img, i) => (
          <div key={i} className="h-48 md:h-64 overflow-hidden">
            <img
              src={img}
              alt="Property"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
          </div>
        ))}
      </div>
    </section>

    {/* CTA */}
    <section className="py-24 px-6" style={{ backgroundColor: "#0B1628" }}>
      <div className="max-w-3xl mx-auto text-center">
        <h2
          className="text-2xl md:text-3xl font-bold text-white mb-5"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Ready to Make Your Next Move?
        </h2>
        <p className="text-sm leading-relaxed mb-10" style={{ color: "#666", maxWidth: "480px", margin: "0 auto 2.5rem" }}>
          Whether you're buying, selling, or exploring your options, let's create a strategy that works.
        </p>
        <Link
          to="/contact"
          className="text-sm text-white border border-white/30 px-10 py-3 tracking-wider uppercase hover:bg-white hover:text-gray-900 transition-all duration-300 inline-block"
        >
          Contact Adris Davis Realty
        </Link>
      </div>
    </section>

    <SiteFooter />
  </>
);

export default RealtyPage;
